import { Component, Prop, h, Element, State, Host, Watch, Event, EventEmitter, Listen } from '@stencil/core';
import { FormValue, RowValue, Root, PredefinedValues } from './types';
import debounce from 'debounce';
import { minus } from 'pn-design-assets/pn-assets/icons.js';

@Component({
  tag: 'pn-dropdown-with-multi-input-rows-row',
  styleUrl: 'pn-dropdown-with-multi-input-rows-row.scss',
})
export class PnDropdownWithMultiInputRowsRow {
  @Element() hostElement: Element;
  private _predefinedValue: PredefinedValues;
  pnFirstInputField: HTMLElement = null;
  pnSecondInputField: HTMLElement = null;
  pnDatePicker: HTMLElement = null;
  pnDeleteRowButton: HTMLElement = null;

  @Prop() languageCode: string = null;
  @Prop() firstInputPlaceholder: string = null;
  @Prop() firstInputMin: number = null;
  @Prop() firstInputMax: number = null;
  @Prop() firstInputWarning: number = null;
  @Prop() firstInputWarningText: string = null;
  @Prop() firstInputLowErrorText: string = null;
  @Prop() firstInputHighErrorText: string = null;
  @Prop() secondInputPlaceholder: string = null;
  @Prop() secondInputMin: number = null;
  @Prop() secondInputMax: number = null;
  @Prop() secondInputWarning: number = null;
  @Prop() secondInputWarningText: string = null;
  @Prop() secondInputLowErrorText: string = null;
  @Prop() secondInputHighErrorText: string = null;
  @Prop() dateLabel: string = null;
  @Prop() datePlaceholder: string = null;
  @Prop() firstValidDate: string = null;
  @Prop() lastValidDate: string = null;
  @Prop() predefinedValue: string = null;
  @Prop() rowIndex: number = null;
  @Prop() minRowCount: number = null;
  @Prop() disabledDates: string = null;

  @State() showFirstInputWarning: boolean = false;
  @State() showFirstInputLowError: boolean = false;
  @State() showFirstInputHighError: boolean = false;
  @State() showSecondInputWarning: boolean = false;
  @State() showSecondInputLowError: boolean = false;
  @State() showSecondInputHighError: boolean = false;

  @State() firstInputValue: string = '';
  @State() secondInputValue: string = '';
  @State() dateValue: string = '';

  @Event() rowValueChange: EventEmitter<PredefinedValues>;
  @Event() deleteRowEvent: EventEmitter<string>;
  @Event() firstInputChange: EventEmitter<PredefinedValues>;
  @Event() secondInputChange: EventEmitter<PredefinedValues>;

  componentWillLoad() {
    this.getPredefinedValues();
    this.setInitialValues();
  }

  componentDidLoad() {
    this.addEventHandlers();
  }

  getPredefinedValues() {
    this._predefinedValue = JSON.parse(this.predefinedValue);
  }

  @Watch('predefinedValue')
  applyPredefinedValue() {
    this.getPredefinedValues();
    this.setInitialValues();

    if (this.firstInputValue?.length) {
      this.validateFirstInput(parseInt(this.firstInputValue));
    }

    if (this.secondInputValue?.length) {
      this.validateSecondInput(parseInt(this.secondInputValue));
    }

    if (this.rowIndex >= this.minRowCount) {
      this.pnDeleteRowButton.addEventListener('click', this.deleteRow.bind(this));
    }
  }

  setInitialValues() {
    this.firstInputValue = this._predefinedValue.firstInput;
    this.secondInputValue = this._predefinedValue.secondInput;
    this.dateValue = this._predefinedValue.date;
  }

  addEventHandlers() {
    const emmitValues = debounce(this.emitRowValues.bind(this), 500);
    this.pnFirstInputField.addEventListener('keyup', debounce(this.handleFirstInput.bind(this), 500));
    this.pnSecondInputField.addEventListener('keyup', debounce(this.handleSecondInput.bind(this), 500));
    this.pnDatePicker.addEventListener('dateselection', this.handleDateChange.bind(this));
    this.pnDatePicker.addEventListener('dateselection', emmitValues);
  }

  handleFirstInput(e) {
    const elm = e.target;
    const value = parseInt(elm.value);

    if (value > 0) {
      this.firstInputValue = elm.value;
      this.firstInputChange.emit({
        id: this._predefinedValue.id,
        firstInput: this.firstInputValue,
        secondInput: this.secondInputValue,
        date: this.dateValue,
      });
    }

    this.validateFirstInput(value);
    this.emitRowValues();
  }

  handleSecondInput(e) {
    const elm = e.target;
    const value = parseInt(elm.value);

    if (value > 0) {
      this.secondInputValue = elm.value;
      this.secondInputChange.emit({
        id: this._predefinedValue.id,
        firstInput: this.firstInputValue,
        secondInput: this.secondInputValue,
        date: this.dateValue,
      });
    }

    this.validateSecondInput(value);
    this.emitRowValues();
  }

  validateFirstInput(input: number) {
    this.showFirstInputWarning = false;
    this.showFirstInputLowError = false;
    this.showFirstInputHighError = false;

    if (input < this.firstInputWarning) {
      this.showFirstInputWarning = true;
    }

    if (input < this.firstInputMin) {
      this.showFirstInputWarning = false;
      this.showFirstInputLowError = true;
    }

    if (input > this.firstInputMax) {
      this.showFirstInputWarning = false;
      this.showFirstInputLowError = false;
      this.showFirstInputHighError = true;
    }
  }

  validateSecondInput(input: number) {
    this.showSecondInputWarning = false;
    this.showSecondInputLowError = false;
    this.showSecondInputHighError = false;

    if (input < this.secondInputWarning) {
      this.showSecondInputWarning = true;
    }

    if (input < this.secondInputMin) {
      this.showSecondInputWarning = false;
      this.showSecondInputLowError = true;
    }

    if (input > this.secondInputMax) {
      this.showSecondInputWarning = false;
      this.showSecondInputLowError = false;
      this.showSecondInputHighError = true;
    }
  }

  handleDateChange(e) {
    const elm = e.target;
    if (elm === this.pnDatePicker) {
      this.dateValue = e.detail.start;
    }
  }

  emitRowValues() {
    this.rowValueChange.emit({
      id: this._predefinedValue.id,
      firstInput: this.firstInputValue,
      secondInput: this.secondInputValue,
      date: this.dateValue,
    });
  }

  deleteRow() {
    this.deleteRowEvent.emit(this._predefinedValue.id);
  }

  render() {
    return (
      <Host>
        <div class="input-element-row">
          <div class="input-container">
            <pn-input
              ref={el => (this.pnFirstInputField = el as HTMLElement)}
              type="number"
              placeholder={this.firstInputPlaceholder}
              value={this.firstInputValue}
              min="1"
            ></pn-input>
            {this.showFirstInputLowError || this.showFirstInputHighError || this.showFirstInputWarning ? (
              <div class="input-element-message-container">
                {this.showFirstInputLowError ? (
                  <div class="input-element-message-container__error">
                    <small>{this.firstInputLowErrorText}</small>
                  </div>
                ) : null}

                {this.showFirstInputHighError ? (
                  <div class="input-element-message-container__error">
                    <small>{this.firstInputHighErrorText}</small>
                  </div>
                ) : null}

                {this.showFirstInputWarning ? (
                  <div class="input-element-message-container__warning">
                    <small>{this.firstInputWarningText}</small>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div class="input-container">
            <pn-input
              ref={el => (this.pnSecondInputField = el as HTMLElement)}
              type="number"
              placeholder={this.secondInputPlaceholder}
              value={this.secondInputValue}
              min="1"
            ></pn-input>
            {this.showSecondInputLowError || this.showSecondInputHighError || this.showSecondInputWarning ? (
              <div class="input-element-message-container">
                {this.showSecondInputLowError ? (
                  <div class="input-element-message-container__error">
                    <small>{this.secondInputLowErrorText}</small>
                  </div>
                ) : null}

                {this.showSecondInputHighError ? (
                  <div class="input-element-message-container__error">
                    <small>{this.secondInputHighErrorText}</small>
                  </div>
                ) : null}

                {this.showSecondInputWarning ? (
                  <div class="input-element-message-container__warning">
                    <small>{this.secondInputWarningText}</small>
                  </div>
                ) : null}
              </div>
            ) : null}
          </div>

          <div class="input-container">
            <pn-date-picker
              ref={el => (this.pnDatePicker = el as HTMLElement)}
              range="false"
              disable-weekends={true}
              disabled-dates={this.disabledDates}
              min-date={this.firstValidDate}
              max-date={this.lastValidDate}
              language={this.languageCode}
              placeholder={this.datePlaceholder}
              start={this.dateValue?.length ? this.dateValue : null}
            ></pn-date-picker>
          </div>

          {this.rowIndex >= this.minRowCount ? (
            <div class="delete-button-container">
              <pn-button
                ref={el => (this.pnDeleteRowButton = el as HTMLElement)}
                small="true"
                icon={minus}
                appearance="warning"
                index={this.rowIndex}
                onClick={() => {
                  this.deleteRow();
                }}
              >
                Delete
              </pn-button>
            </div>
          ) : null}
        </div>
      </Host>
    );
  }
}
